import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { OathDualSpinner, OathAlert } from '../../shared/OathUI';
import { formatCurrency } from '../../../services/oathAgencyVitalsService';

const formatCompact = (value) => {
  if (value == null || isNaN(value)) return '$0';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000).toLocaleString()}K`;
  return formatCurrency(value);
};

/** Delta badge — colored pill with arrow */
const DeltaBadge = ({ percent, direction, size = 'sm' }) => {
  const up = direction === 'increase';
  const Icon = up ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  const sizeClasses = size === 'lg'
    ? 'px-2.5 py-1 qhd:px-3 qhd:py-1.5 text-xs qhd:text-sm gap-1 qhd:gap-1.5'
    : 'px-1.5 py-0.5 text-[10px] qhd:text-xs gap-0.5 qhd:gap-1';
  const iconSize = size === 'lg' ? 'w-3.5 h-3.5 qhd:w-4 qhd:h-4' : 'w-3 h-3 qhd:w-3.5 qhd:h-3.5';
  return (
    <span className={`inline-flex items-center font-semibold rounded-full ${sizeClasses} ${
      up ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
         : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    }`}>
      <Icon className={iconSize} />
      {up ? '+' : ''}{percent?.toFixed(1) || 0}%
    </span>
  );
};

/**
 * Summary Tab — Agency Business Snapshot
 *
 * Designed for bail agents. Uses bond industry language:
 *   "bonds written" not "powers executed"
 *   "exposure" not "liability"
 *   "production" not "annualized projection"
 *
 * Layout:
 *   Row 1: This month's production (hero) + how we're pacing vs last year
 *   Row 2: YTD | Projected Full Year | Last Year — side by side comparison
 *   Row 3: Agency profile — exposure, unexecuted, lifetime track record
 */
const SummaryTab = ({ summary, summaryLoading, summaryError }) => {
  const hasSummary = !!summary;
  const isUp = summary?.yoyDirection === 'increase';

  if (summaryLoading && !hasSummary) {
    return (
      <div className="flex flex-col items-center justify-center py-12 qhd:py-16">
        <OathDualSpinner />
        <p className="mt-3 text-xs qhd:text-sm text-gray-500 dark:text-gray-400">Loading summary...</p>
      </div>
    );
  }

  if (summaryError && !hasSummary) {
    return (
      <div className="py-6">
        <OathAlert variant="error">Failed to load summary: {summaryError}</OathAlert>
      </div>
    );
  }

  if (!hasSummary) {
    return (
      <div className="py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
        No summary data available for this agency.
      </div>
    );
  }

  const moM = summary.monthComparison;
  const life = summary.lifetime;

  return (
    <div className="space-y-2.5 qhd:space-y-4 4k:space-y-5">

      {/* Row 1: This Month hero + Year-over-Year pacing */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2.5 qhd:gap-4">
        {/* This month — spans 3 cols */}
        <div className="sm:col-span-3 bg-white dark:bg-gray-800 rounded-xl qhd:rounded-2xl shadow-md p-3.5 qhd:p-5 4k:p-6">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[10px] qhd:text-xs 4k:text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">
                {moM?.monthLabel ? `${moM.monthLabel} Production` : 'This Month'}
              </p>
              <p className="text-2xl qhd:text-3xl 4k:text-4xl font-bold text-gray-900 dark:text-white tracking-tight mt-0.5">
                {moM ? formatCompact(moM.currentLiability) : '--'}
              </p>
            </div>
            {moM?.liabilityDeltaPercent != null && (
              <DeltaBadge percent={moM.liabilityDeltaPercent} direction={moM.liabilityDirection} size="lg" />
            )}
          </div>
          {moM && (
            <p className="text-xs qhd:text-sm text-gray-400 dark:text-gray-500 mt-1">
              {moM.currentCount?.toLocaleString() || 0} bonds written
              {moM.previousLiability > 0 && <> &middot; Prior month was {formatCompact(moM.previousLiability)} ({moM.previousCount?.toLocaleString() || 0} bonds)</>}
            </p>
          )}
        </div>

        {/* Year-over-year pacing — spans 2 cols */}
        <div className="sm:col-span-2 bg-white dark:bg-gray-800 rounded-xl qhd:rounded-2xl shadow-md p-3.5 qhd:p-5 4k:p-6 flex flex-col justify-between">
          <p className="text-[10px] qhd:text-xs 4k:text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">
            Pacing vs Last Year
          </p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className={`text-2xl qhd:text-3xl 4k:text-4xl font-bold tracking-tight ${
              isUp ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}>
              {isUp ? '+' : ''}{summary.yoyDeltaPercent?.toFixed(1) || 0}%
            </span>
            {isUp
              ? <ArrowTrendingUpIcon className="w-5 h-5 qhd:w-6 qhd:h-6 text-green-500 dark:text-green-400" />
              : <ArrowTrendingDownIcon className="w-5 h-5 qhd:w-6 qhd:h-6 text-red-500 dark:text-red-400" />
            }
          </div>
          <p className="text-xs qhd:text-sm text-gray-400 dark:text-gray-500 mt-1">
            {isUp ? 'Ahead of' : 'Behind'} last year by {formatCompact(Math.abs(summary.yoyDeltaAmount || 0))}
          </p>
        </div>
      </div>

      {/* Row 2: YTD | Projected | Last Year — clean comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800 rounded-xl qhd:rounded-2xl shadow-md overflow-hidden">
        <div className="p-3 qhd:p-4 4k:p-5 text-center">
          <p className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Year-to-Date</p>
          <p className="text-lg qhd:text-xl 4k:text-2xl font-bold text-primary dark:text-primary-light mt-0.5">{formatCompact(summary.ytdLiability)}</p>
          <p className="text-[10px] qhd:text-xs text-gray-400 dark:text-gray-500">{summary.ytdCount?.toLocaleString() || 0} bonds</p>
        </div>
        <div className="p-3 qhd:p-4 4k:p-5 text-center">
          <p className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">On Pace For</p>
          <p className="text-lg qhd:text-xl 4k:text-2xl font-bold text-orange-600 dark:text-orange-400 mt-0.5">{formatCompact(summary.annualizedProjection)}</p>
          <p className="text-[10px] qhd:text-xs text-gray-400 dark:text-gray-500">full year projected</p>
        </div>
        <div className="p-3 qhd:p-4 4k:p-5 text-center">
          <p className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Last Year</p>
          <p className="text-lg qhd:text-xl 4k:text-2xl font-bold text-gray-500 dark:text-gray-400 mt-0.5">{formatCompact(summary.previousYearLiability)}</p>
          <p className="text-[10px] qhd:text-xs text-gray-400 dark:text-gray-500">total volume</p>
        </div>
      </div>

      {/* Row 3: Agency profile — exposure + track record */}
      <div className="grid grid-cols-2 sm:grid-cols-4 bg-white dark:bg-gray-800 rounded-xl qhd:rounded-2xl shadow-md overflow-hidden
        [&>*]:border-b [&>*]:border-gray-100 [&>*]:dark:border-gray-700 [&>*:nth-child(odd)]:border-r sm:[&>*]:border-b-0 sm:[&>*]:border-r sm:[&>*:last-child]:border-r-0">
        <div className="p-3 qhd:p-4 4k:p-5">
          <p className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Open Exposure</p>
          <p className="text-base qhd:text-lg 4k:text-xl font-bold text-gray-900 dark:text-white mt-0.5">{formatCompact(summary.openLiability)}</p>
        </div>
        <div className="p-3 qhd:p-4 4k:p-5">
          <p className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Unexecuted</p>
          <p className="text-base qhd:text-lg 4k:text-xl font-bold text-gray-900 dark:text-white mt-0.5">{summary.unexecutedPowersCount ?? 0}</p>
          <p className="text-[10px] qhd:text-xs text-gray-400 dark:text-gray-500">{formatCompact(summary.unexecutedPowersAmount)}</p>
        </div>
        <div className="p-3 qhd:p-4 4k:p-5">
          <p className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Lifetime Bonds</p>
          <p className="text-base qhd:text-lg 4k:text-xl font-bold text-gray-900 dark:text-white mt-0.5">{life?.totalCount?.toLocaleString() || 0}</p>
          <p className="text-[10px] qhd:text-xs text-gray-400 dark:text-gray-500">{formatCompact(life?.totalLiability)} total</p>
        </div>
        <div className="p-3 qhd:p-4 4k:p-5">
          <p className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Years Active</p>
          <p className="text-base qhd:text-lg 4k:text-xl font-bold text-gray-900 dark:text-white mt-0.5">{life?.yearsActive || 0}</p>
          <p className="text-[10px] qhd:text-xs text-gray-400 dark:text-gray-500">
            {life?.yearsActive > 0 && <>since {new Date().getFullYear() - life.yearsActive}</>}
          </p>
        </div>
      </div>

      {/* Row 4: Brief narrative — conversational, bail-agent language */}
      <div className="flex items-start gap-2.5 qhd:gap-3 bg-white dark:bg-gray-800 shadow-md rounded-xl qhd:rounded-2xl px-3.5 py-2.5 qhd:px-5 qhd:py-3.5 4k:px-6 4k:py-4 border-l-4 border-l-primary dark:border-l-primary-light">
        <ShieldCheckIcon className="w-4 h-4 qhd:w-5 qhd:h-5 text-primary dark:text-primary-light flex-shrink-0 mt-0.5" />
        <p className="text-xs qhd:text-sm 4k:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
          {summary.agencyName && <><strong className="text-gray-900 dark:text-white">{summary.agencyName}</strong> — </>}
          {moM ? (
            <>You wrote <strong>{moM.currentCount?.toLocaleString() || 0} bonds</strong> in {moM.monthLabel} for {formatCompact(moM.currentLiability)} in new exposure
            {moM.liabilityDeltaPercent != null && (
              <> (<span className={moM.liabilityDirection === 'increase' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {moM.liabilityDirection === 'increase' ? 'up' : 'down'} {Math.abs(moM.liabilityDeltaPercent).toFixed(1)}%
              </span> from the month before)</>
            )}. </>
          ) : (
            <>No bond production data for the current month yet. </>
          )}
          So far this year, you're at <strong>{formatCompact(summary.ytdLiability)}</strong> across {summary.ytdCount?.toLocaleString() || 0} bonds.
          {summary.previousYearLiability > 0 && (
            <> At this pace, you're on track for <strong className="text-orange-600 dark:text-orange-400">{formatCompact(summary.annualizedProjection)}</strong> this year
            — {isUp ? 'ahead of' : 'behind'} last year's {formatCompact(summary.previousYearLiability)} by{' '}
            <strong className={isUp ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
              {summary.yoyDeltaPercent?.toFixed(1) || 0}%
            </strong>.</>
          )}
          {life?.yearsActive > 0 && (
            <> Over {life.yearsActive} years in business, you've written {life.totalCount?.toLocaleString() || 0} bonds
            totaling {formatCompact(life.totalLiability)} in exposure.</>
          )}
        </p>
      </div>
    </div>
  );
};

export default SummaryTab;
